import { useEffect, useState } from "react"

import { getContracts } from "../../Contracts/api/services.ts"
import { getInvoices } from "../../Project/api/project-invoices.services.ts"
import type { RelatedRecordType } from "../../Project/types/project-documents.types.ts"
import { getProposals } from "../../Proposals/api/services.ts"

export interface RelatedRecordOption {
  label: string
  value: string
}

type LoadResult = { options: RelatedRecordOption[]; failed: boolean }

const failed: LoadResult = { options: [], failed: true }

/**
 * This project's own records of the given type — Project resolves to itself
 * with no request, everything else is a same-project search.
 */
export function useRelatedRecordOptions(
  recordType: RelatedRecordType,
  projectId: string,
  projectName: string
) {
  const [options, setOptions] = useState<RelatedRecordOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    // Lead and Project are two proxies over the exact same `core.Project`
    // row (EAD-615) — there is no separate "original lead" row a converted
    // project's document could point at instead, so this resolves to the
    // same single own-record option "project" already does. Fixes a real,
    // previously-shipped bug: with no branch here at all, picking "Lead"
    // silently fell through to the `invoice` branch below and searched
    // invoices instead.
    if (recordType === "project" || recordType === "lead") {
      setOptions([{ label: projectName, value: projectId }])
      setLoading(false)
      setLoadFailed(false)
      return
    }
    let cancelled = false
    setLoading(true)

    const load = async (): Promise<LoadResult> => {
      if (recordType === "proposal") {
        const response = await getProposals({ lead_id: projectId })
        return response.ok && response.data
          ? {
              options: response.data.map((p) => ({
                label: p.proposal_id,
                value: p.id
              })),
              failed: false
            }
          : failed
      }
      if (recordType === "contract") {
        const response = await getContracts({ lead_id: projectId })
        return response.ok && response.data
          ? {
              options: response.data.map((c) => ({
                label: c.contract_id,
                value: c.id
              })),
              failed: false
            }
          : failed
      }
      const response = await getInvoices({
        project: projectId,
        ordering: null,
        type: null,
        status: null,
        page: 1,
        page_size: 100
      })
      return response.ok && response.data
        ? {
            options: response.data.results.map((i) => ({
              label: i.invoice_number,
              value: i.id
            })),
            failed: false
          }
        : failed
    }

    void load().then((result) => {
      if (cancelled) return
      setOptions(result.options)
      setLoadFailed(result.failed)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [recordType, projectId, projectName])

  return { options, loading, loadFailed }
}
